export type SnowLossLookup = {
  convertStateToAbbr: (input: string, to: string) => string | undefined;
  getSnowLoss: (stateAbbr: string) => string | null | undefined;
};

const isLossObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const LOSS_KEY_ALIASES: Record<string, string> = {
  soiling: 'soiling',
  Soiling: 'soiling',
  shading: 'shading',
  Shading: 'shading',
  snow: 'snow',
  Snow: 'snow',
  show: 'snow',
  mismatch: 'mismatch',
  Mismatch: 'mismatch',
  wiring: 'wiring',
  Wiring: 'wiring',
  connections: 'connections',
  Connections: 'connections',
  lid: 'lid',
  lightInducedDegradation: 'lid',
  light_induced_degradation: 'lid',
  Light_Induced_Degradation: 'lid',
  nameplate_rating: 'nameplate_rating',
  nameplateRating: 'nameplate_rating',
  Nameplate_Rating: 'nameplate_rating',
  age: 'age',
  Age: 'age',
  availability: 'availability',
  Availability: 'availability'
};

const assignLosses = (target: Record<string, unknown>, source: unknown) => {
  if (!isLossObject(source)) return;
  Object.entries(source).forEach(([key, value]) => {
    target[LOSS_KEY_ALIASES[key] || key] = value;
  });
};

const shouldApplyStateSnow = (
  currentSnow: unknown,
  forceStateSnow: boolean
) => {
  if (forceStateSnow) return true;
  if (currentSnow === undefined || currentSnow === null || currentSnow === '') {
    return true;
  }

  const numericSnow = Number(currentSnow);
  return Number.isFinite(numericSnow) && numericSnow === 0;
};

export const getStateSnowLoss = (
  state: unknown,
  lookup: SnowLossLookup
): string | null => {
  if (!state || typeof state !== 'string') return null;

  const trimmed = state.trim();
  if (!trimmed) return null;

  const stateAbbr = /^[A-Za-z]{2}$/.test(trimmed)
    ? trimmed.toUpperCase()
    : lookup.convertStateToAbbr(trimmed, 'abbr');

  if (!stateAbbr) return null;
  const snow = lookup.getSnowLoss(stateAbbr);
  return snow === undefined ? null : snow;
};

export const buildEffectiveSystemLosses = (
  {
    baseLosses = null,
    canvasLosses = null,
    iframeLosses = null,
    lead = null,
    forceStateSnow = false,
    snowLookup
  }: {
    baseLosses?: unknown;
    canvasLosses?: unknown;
    iframeLosses?: unknown;
    lead?: any;
    forceStateSnow?: boolean;
    snowLookup: SnowLossLookup;
  } = {
    snowLookup: {
      convertStateToAbbr: () => undefined,
      getSnowLoss: () => null
    }
  }
) => {
  const effectiveLosses: Record<string, unknown> = {};

  [baseLosses, canvasLosses, iframeLosses].forEach((losses) => {
    assignLosses(effectiveLosses, losses);
  });

  const snowLoss = getStateSnowLoss(
    lead?.property?.state || lead?.state,
    snowLookup
  );
  if (
    snowLoss !== null &&
    snowLoss !== undefined &&
    shouldApplyStateSnow(effectiveLosses.snow, forceStateSnow)
  ) {
    effectiveLosses.snow = snowLoss;
  }

  return effectiveLosses;
};
